import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid3x3, Wrench, Tag, Calendar, Users, TrendingUp, IndianRupee, RefreshCw } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import AdminLayout from '../../components/AdminLayout';

const AdminDashboard = () => {
  const nav = useNavigate();
  const { categories, services, offers, bookings, users, resetAll } = useApp();

  const totalServices = Object.values(services).reduce((s, cat) => s + (cat.items?.length || 0), 0);
  const totalRevenue = bookings.reduce((s, b) => s + (b.total || 0), 0);

  const stats = [
    { label: 'Categories', value: categories.length, icon: Grid3x3, color: '#2E7D32', bg: '#E8F5E9', to: '/admin/categories' },
    { label: 'Services', value: totalServices, icon: Wrench, color: '#1E88E5', bg: '#E3F2FD', to: '/admin/services' },
    { label: 'Active Offers', value: offers.length, icon: Tag, color: '#EA580C', bg: '#FFF3E0', to: '/admin/offers' },
    { label: 'Bookings', value: bookings.length, icon: Calendar, color: '#7B1FA2', bg: '#F3E5F5', to: '/admin/bookings' },
    { label: 'Users', value: users.length, icon: Users, color: '#00838F', bg: '#E0F7FA', to: '/admin/users' },
    { label: 'Revenue', value: `₹${totalRevenue.toLocaleString('en-IN')}`, icon: IndianRupee, color: '#D97706', bg: '#FEF3C7' },
  ];

  const handleReset = () => {
    if (window.confirm('Reset all catalog data (categories/services/offers) to defaults? This cannot be undone.')) {
      resetAll();
    }
  };

  return (
    <AdminLayout title="Dashboard">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {stats.map((s) => (
          <button key={s.label} onClick={() => s.to && nav(s.to)} disabled={!s.to}
            className="bg-white border border-neutral-200 rounded-xl p-4 md:p-5 text-left hover:shadow-md transition-shadow disabled:cursor-default">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: s.bg }}>
              <s.icon className="w-5 h-5" style={{ color: s.color }} />
            </div>
            <p className="mt-3 text-xs text-neutral-500 font-medium">{s.label}</p>
            <p className="font-extrabold text-2xl mt-0.5">{s.value}</p>
          </button>
        ))}
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold">Recent bookings</h2>
            <button onClick={() => nav('/admin/bookings')} className="text-xs text-[#2E7D32] font-semibold hover:underline">View all</button>
          </div>
          {bookings.length === 0 ? (
            <p className="text-sm text-neutral-500 py-6 text-center">No bookings yet</p>
          ) : (
            <div className="space-y-2">
              {bookings.slice(0, 5).map((b) => (
                <div key={b.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-neutral-50">
                  <img src={b.items[0]?.image} className="w-10 h-10 rounded object-cover" alt="" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{b.items[0]?.name}</p>
                    <p className="text-xs text-neutral-500">{b.userName || 'Guest'} • {new Date(b.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}</p>
                  </div>
                  <p className="font-bold text-sm text-[#EA580C]">₹{b.total}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold">Quick actions</h2>
            <TrendingUp className="w-4 h-4 text-[#2E7D32]" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => nav('/admin/categories')} className="p-4 rounded-lg border border-neutral-200 text-left hover:border-[#2E7D32] hover:bg-[#E8F5E9] transition-colors">
              <Grid3x3 className="w-5 h-5 text-[#2E7D32]" />
              <p className="mt-2 font-semibold text-sm">Edit Categories</p>
              <p className="text-xs text-neutral-500 mt-0.5">Change photos & names</p>
            </button>
            <button onClick={() => nav('/admin/services')} className="p-4 rounded-lg border border-neutral-200 text-left hover:border-[#2E7D32] hover:bg-[#E8F5E9] transition-colors">
              <Wrench className="w-5 h-5 text-[#2E7D32]" />
              <p className="mt-2 font-semibold text-sm">Manage Services</p>
              <p className="text-xs text-neutral-500 mt-0.5">Add / edit / delete</p>
            </button>
            <button onClick={() => nav('/admin/offers')} className="p-4 rounded-lg border border-neutral-200 text-left hover:border-[#2E7D32] hover:bg-[#E8F5E9] transition-colors">
              <Tag className="w-5 h-5 text-[#2E7D32]" />
              <p className="mt-2 font-semibold text-sm">Manage Offers</p>
              <p className="text-xs text-neutral-500 mt-0.5">Coupons & discounts</p>
            </button>
            <button onClick={handleReset} className="p-4 rounded-lg border border-red-200 text-left hover:bg-red-50 transition-colors">
              <RefreshCw className="w-5 h-5 text-red-600" />
              <p className="mt-2 font-semibold text-sm text-red-700">Reset catalog</p>
              <p className="text-xs text-red-500 mt-0.5">Restore defaults</p>
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
